import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import { Spinner } from './spinner';

const buttonVariants = cva(
  'font-mono inline-flex items-center justify-center whitespace-nowrap rounded-xs text-sm font-bold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline hover:text-primary/80',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = ({ className, variant, loading, children, size, disabled, asChild = false, ...props }: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';

  return (
    // A loading button is always disabled, whatever the caller passes: it is
    // what stops a second click on e.g. "Pay" while the first is in flight.
    <Comp disabled={loading || disabled} className={cn(buttonVariants({ variant, size, className }))} {...props}>
      {/* A Slot needs exactly one child element, so an `asChild` button cannot carry the spinner. */}
      {asChild ? (
        children
      ) : (
        <>
          {loading && <Spinner className='w-4 h-4 mr-2' />}
          {children}
        </>
      )}
    </Comp>
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
